import PropTypes from 'prop-types';
import { noop } from 'lodash/fp';
import classnames from 'classnames/bind';
import { Icon } from 'app/components/core/Icon';
import { useTheme } from 'app/components/core/ThemeProvider';
import * as styles from './LinkButton.css';

const cx = classnames.bind(styles);

export function LinkButton({
    testId = null,
    className = null,
    onClick = noop,
    underline = false,
    colorless = false,
    icon = null,
    iconOffset = null,
    children,
    ...props
}) {
    const { themeClassname } = useTheme();
    const classes = cx('base', { underline, colorless }, className, themeClassname);

    return (
        <button
            data-testid={testId}
            className={classes}
            onClick={onClick}
            style={{ '--link-button-icon-offset': iconOffset }}
            {...props}>
            {icon && <Icon className={styles.icon} variant={icon} inline />}
            <span className={styles.content}>{children}</span>
        </button>
    );
}

LinkButton.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * Callback triggered when clicking the button.
     */
    onClick: PropTypes.func,

    /**
     * By default links only reveal the underline on hover. Setting this to
     * `true` shows the underline always, effectively removing the visible
     * state change on hover.
     */
    underline: PropTypes.bool,

    /**
     * When `true`, removes the default link color and inherits whatever the
     * color is from the parent.
     */
    colorless: PropTypes.bool,

    /**
     * An optional icon variant to display to the left of the button text.
     */
    icon: PropTypes.string,

    /**
     * Allows the vertical placement of the icon to be adjusted. This is useful
     * for non-square icons that need to be moved to appear to rest on the
     * baseline, for example. Should be a CSS compatible length.
     */
    iconOffset: PropTypes.string,

    /**
     * LinkButton contents. Unlike a standard Button, this should not be empty.
     */
    children: PropTypes.node.isRequired
};
